/*
 * Tittli product page behaviour. One module for everything page-specific:
 *
 * - <tittli-pdp-gallery>: Blossom's PdpGallery engine (native scroll-snap
 *   track, the active slide read from scroll position with smooth-scroll
 *   locking, height following each photo's ratio) plus a zoom dialog. It does
 *   not follow the selected variant — see the note in connectedCallback.
 * - Sticky buy bar (phones): shown once the main ADD TO CART has scrolled
 *   above the viewport.
 * - Every duplicate ADD TO CART (sticky bar, bottom row) follows the
 *   selected variant's availability.
 * - Coupon copy (Blossom's clipboard → textarea → select fallback chain).
 * - Size guide sheet.
 *
 * Dawn's `subscribe` / `PUB_SUB_EVENTS` come from its deferred pubsub.js and
 * constants.js; this module is deferred too and sits later in the document,
 * so they exist by the time it runs.
 */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const onVariantChange = (handler) => {
  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
    return window.subscribe(window.PUB_SUB_EVENTS.variantChange, handler);
  }
  return null;
};

/* ---------- gallery ---------- */

class TittliPdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    if (!this.track || !this.slides.length) return;

    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
    this.rail = this.querySelector('[data-gallery-thumbs]');
    this.current = this.querySelector('[data-gallery-current]');
    this.index = Number(this.dataset.startIndex) || 0;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index))));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });

    // Read the active slide from a native swipe; ignore the in-between
    // positions of an arrow/thumb smooth scroll until it settles.
    this.track.addEventListener('scroll', () => this.scrollLock || this.syncFromScroll(), { passive: true });
    this.track.addEventListener('scrollend', () => {
      this.scrollLock = false;
      this.syncFromScroll();
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.fitHeight());
      this.resizeObserver.observe(this.track);
    }

    /* The gallery deliberately does not follow the variant. On this store the
     * colours are separate products and the variants are sizes, so a
     * variant's featured image is whichever photo happened to be attached to
     * it in admin — jumping there on a size change moved the shopper to an
     * unrelated picture of the same garment. See snippets/tittli-pdp-gallery.
     */

    this.setupZoom();

    if (this.index > 0) this.goTo(this.index, false);
    else this.setActive(0);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    clearTimeout(this.lockTimer);
  }

  step(direction) {
    const count = this.slides.length;
    this.goTo((this.index + direction + count) % count);
  }

  goTo(index, smooth = true) {
    const slide = this.slides[index];
    if (!slide) return;
    const animate = smooth && !reducedMotion();
    clearTimeout(this.lockTimer);
    this.scrollLock = animate;
    if (animate) this.lockTimer = setTimeout(() => (this.scrollLock = false), 900);
    this.track.scrollTo({ left: slide.offsetLeft, behavior: animate ? 'smooth' : 'auto' });
    this.setActive(index);
  }

  syncFromScroll() {
    const width = this.track.clientWidth;
    if (!width) return;
    const index = Math.round(this.track.scrollLeft / width);
    if (this.slides[index]) this.setActive(index);
  }

  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;
    this.index = index;

    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    if (this.current) this.current.textContent = index + 1;

    this.slides.forEach((slide, i) => {
      if (i !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });

    // Keep the active thumbnail in view inside its rail, without moving the page.
    const thumb = this.thumbs[index];
    if (this.rail && thumb && this.rail.scrollWidth > this.rail.clientWidth) {
      this.rail.scrollTo({
        left: thumb.offsetLeft - (this.rail.clientWidth - thumb.clientWidth) / 2,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    this.fitHeight();
  }

  /* The stage takes the active photo's own ratio — nothing is cropped. */
  fitHeight() {
    /* Phones stack the gallery instead of sliding it, and a stacked column
     * has to size itself: pinning the track to one photo's height leaves the
     * other four overflowing onto the title and the buy button.
     *
     * The layout is detected from the track's own overflow rather than a
     * media query, so the breakpoint stays in the stylesheet that sets it and
     * this never drifts out of sync with it.
     */
    if (getComputedStyle(this.track).overflowX === 'visible') {
      this.track.style.removeProperty('height');
      this.track.style.removeProperty('aspect-ratio');
      return;
    }

    const slide = this.slides[this.index];
    const ratio = slide ? parseFloat(slide.dataset.ratio) : 0;
    const width = this.track.clientWidth;
    if (!ratio || !width) return;
    this.track.style.aspectRatio = 'auto';
    this.track.style.height = `${Math.round(width / ratio)}px`;
  }

  setupZoom() {
    const button = this.querySelector('[data-gallery-zoom]');
    const dialog = this.querySelector('[data-gallery-zoom-dialog]');
    const image = this.querySelector('[data-gallery-zoom-img]');
    if (!button || !dialog || !image) return;

    button.addEventListener('click', () => {
      const slide = this.slides[this.index];
      image.src = slide?.dataset.zoomSrc || '';
      image.alt = slide?.querySelector('img')?.alt || '';
      dialog.showModal();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-gallery-zoom-close]')) dialog.close();
    });
    dialog.addEventListener('close', () => button.focus());
  }
}

if (!customElements.get('tittli-pdp-gallery')) {
  customElements.define('tittli-pdp-gallery', TittliPdpGallery);
}

/* ---------- page-level wiring (once) ---------- */

if (!window.__tittliProduct) {
  window.__tittliProduct = true;

  const initStickyBar = () => {
    const bar = document.querySelector('[data-pdp-sticky]');
    const anchor = document.querySelector('[data-pdp-buy]');
    if (!bar || !anchor || bar.dataset.ready) return;
    bar.dataset.ready = 'true';

    const setVisible = (visible) => {
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', String(!visible));
      const button = bar.querySelector('[data-pdp-submit]');
      if (button) button.tabIndex = visible ? 0 : -1;
    };

    /* The root is stretched far below the viewport, so the anchor counts as
     * "intersecting" anywhere from the top edge downwards and only stops once
     * it has scrolled off the top. On a phone the button starts below the
     * fold; with a plain viewport root it starts "not intersecting", and a
     * fast fling (or a jump to an anchor, or back-navigation scroll restore)
     * that lands it straight above the viewport is *still* "not
     * intersecting" — no state change, no callback, no bar. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), {
        rootMargin: '0px 0px 1000000px 0px',
      }).observe(anchor);
    }
  };

  // Keep every extra ADD TO CART and the sticky price in step with the
  // variant product-info.js just rendered.
  onVariantChange(({ data }) => {
    const available = Boolean(data?.variant?.available);
    document.querySelectorAll('[data-pdp-submit]').forEach((button) => {
      button.disabled = !available;
      button.textContent = available ? window.variantStrings?.addToCart : window.variantStrings?.soldOut;
    });

    const fresh = data?.html?.querySelector('[data-pdp-sticky-price]');
    const current = document.querySelector('[data-pdp-sticky-price]');
    if (fresh && current) current.innerHTML = fresh.innerHTML;
  });

  /* Coupon copy: Clipboard API first; if it's missing or refuses (insecure
   * context, some in-app browsers), a hidden textarea + execCommand; if that
   * fails too, select the code so it's one long-press away. */
  const copyWithTextarea = (text) => {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    if (!ok) throw new Error('copy failed');
  };

  document.addEventListener('click', async (event) => {
    const copy = event.target.closest('[data-copy-code]');
    if (copy) {
      const label = copy.querySelector('[data-copy-label]');
      try {
        if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(copy.dataset.copyCode);
        else copyWithTextarea(copy.dataset.copyCode);
        copy.classList.add('is-copied');
        if (label) label.textContent = copy.dataset.labelCopied;
      } catch (error) {
        const code = copy.closest('.tittli-coupon')?.querySelector('.tittli-coupon__code');
        if (code) {
          const range = document.createRange();
          range.selectNodeContents(code);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
        }
      }
      clearTimeout(copy.copyTimer);
      copy.copyTimer = setTimeout(() => {
        copy.classList.remove('is-copied');
        if (label) label.textContent = copy.dataset.labelCopy;
      }, 1800);
      return;
    }
  });

  /* The colourways are one scrolling row, and the colour being viewed is as
   * likely to be the fifth as the first. Left alone the row opens at its start
   * and the selected thumbnail sits off-screen, so the shopper cannot see
   * which colour they are looking at.
   *
   * scrollLeft is set directly rather than calling scrollIntoView: that method
   * walks up every scrollable ancestor and would move the page itself, landing
   * the shopper partway down the product instead of at the top of it. Instant,
   * not smooth — this is the state the page should open in, not an animation
   * to watch. And only when the row actually overflows, so a product with two
   * colours is left alone.
   */
  const centreColourway = () => {
    document.querySelectorAll('.tittli-colorways__list').forEach((list) => {
      const block = list.closest('.tittli-colorways');
      const rail = list.closest('.tittli-colorways__rail');
      const nav = block?.querySelector('[data-colorways-nav]');
      const prev = nav?.querySelector('[data-colorways-prev]');
      const next = nav?.querySelector('[data-colorways-next]');

      const overflows = () => list.scrollWidth > list.clientWidth + 4;

      /* The arrows and the rail's two fades say the same thing twice, on
       * purpose: the arrows are the affordance on a mouse, the fades are what
       * is left to go by once a thumb is covering the row. Both are driven
       * from the one measurement so they can never disagree. */
      const sync = () => {
        const more = overflows();
        if (nav) nav.hidden = !more;
        if (!more) {
          rail?.classList.remove('has-before', 'has-after');
          return;
        }

        /* Math.abs because scrollLeft runs negative in a right-to-left
         * document, where the row still starts at its own beginning. */
        const position = Math.abs(list.scrollLeft);
        const atStart = position <= 4;
        const atEnd = position >= list.scrollWidth - list.clientWidth - 4;
        if (prev) prev.disabled = atStart;
        if (next) next.disabled = atEnd;
        rail?.classList.toggle('has-before', !atStart);
        rail?.classList.toggle('has-after', !atEnd);
      };

      if (!list.dataset.railReady) {
        list.dataset.railReady = 'true';
        /* 80% of the visible row, so a card stays in view as a sense of place
         * — the same step the product rails take. */
        const page = (direction) =>
          list.scrollBy({
            left: direction * list.clientWidth * 0.8,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        prev?.addEventListener('click', () => page(-1));
        next?.addEventListener('click', () => page(1));
        list.addEventListener('scroll', sync, { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(sync).observe(list);
      }

      const current = list.querySelector('.is-current');
      if (current && overflows()) {
        /* Measured against the list rather than read off offsetLeft, which is
         * relative to whichever ancestor happens to be positioned. */
        const item = current.closest('li') || current;
        const listBox = list.getBoundingClientRect();
        const itemBox = item.getBoundingClientRect();
        list.scrollLeft += itemBox.left - listBox.left - (list.clientWidth - itemBox.width) / 2;
      }

      sync();
    });
  };

  /* The coupon's Apply control.
   *
   * It applies nothing. WELCOME10 is an automatic discount: Shopify takes it
   * off by itself once the cart clears the minimum, and posting it as a code
   * is refused because no code by that name exists. What the shopper is
   * missing is not the discount, it is knowing what they will pay — so the
   * button reveals the figure the page already worked out, and says so.
   *
   * The choice is remembered for the session because choosing a size reloads
   * the page. Without that, applying the offer and then picking a size would
   * silently undo it.
   */
  /* What is remembered is the refusal, not the acceptance.
   *
   * The offer used to be opt-in: the price showed the full figure until the
   * button was pressed. But the discount is already the shopper's — Shopify
   * takes it off by itself once the cart clears the minimum — so the full
   * price was the one number on the page that nobody was ever going to pay,
   * and the button was asking permission to tell the truth.
   *
   * It is on from the start now, and the stored value is the code someone
   * pressed Remove on. Only that is worth carrying between page loads, and
   * choosing a size reloads this page, so without it a shopper who wanted the
   * original price back would lose it on their next tap. */
  const COUPON_DISMISSED_KEY = 'tittli-coupon-dismissed';

  const initCoupons = () => {
    document.querySelectorAll('[data-coupon-card]').forEach((card) => {
      const button = card.querySelector('[data-coupon-apply]');
      const result = card.querySelector('[data-coupon-result]');
      if (!button || !result) return;

      const code = (card.dataset.couponCode || '').toUpperCase();

      /* The headline price becomes the price with the offer, and the "N% OFF"
       * badge follows it — the badge is measured against the compare-at price,
       * so leaving it alone would have it claiming 65% beside a price that is
       * now 68% off. Every figure was rendered by Liquid, so this swaps text
       * rather than doing arithmetic the cart might round differently. */
      const priceBlock = document.querySelector('[data-price-offer]');
      const priceValue = priceBlock?.querySelector('[data-price-current]');
      const priceOff = priceBlock?.querySelector('[data-price-off]');
      const priceNote = document.querySelector('[data-price-note]');
      const remove = card.querySelector('[data-coupon-remove]');

      /* The price counts between the two figures rather than cutting to the
       * new one. A number that simply changes is easy to miss; one that travels
       * is the whole point of tapping the button.
       *
       * Only whole rupees are shown while it runs — paise ticking past at
       * sixty frames a second is noise — and the last frame is replaced with
       * the string Liquid rendered, so what the shopper is left looking at is
       * the server's own formatting and never a figure the browser rounded.
       */
      const DURATION = 1100;

      /* Paise are shown for every frame of the count, even though the resting
       * price usually has none. A price of ₹999 discounts to ₹899.10, so the
       * decimals have to appear at some point — and appearing on the first
       * moving frame is invisible, where appearing on the last one is a jump
       * in width at the exact moment the eye settles on the number. */
      const money = new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      let tween = null;

      const countTo = (fromPaise, toPaise, settleText) => {
        cancelAnimationFrame(tween);

        const skip =
          !priceValue ||
          !Number.isFinite(fromPaise) ||
          !Number.isFinite(toPaise) ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (skip) {
          if (priceValue) {
            priceValue.textContent = settleText;
            priceValue.classList.remove('is-counting');
          }
          return;
        }

        /* Hold the width still for the run.
         *
         * font-variant-numeric: tabular-nums is set on this element and does
         * nothing, because the theme's Poppins does not ship the tnum feature
         * — measured on the page, the width wandered across 18px as the digits
         * changed. The row is centred, so that wander moves the price and the
         * struck price and the badge beside it on nearly every frame.
         *
         * So the widest of the two endpoints is measured once and pinned for
         * the duration. Intermediate values are never wider than the figure
         * they started from or the one they are heading to.
         */
        const widest = (() => {
          const original = priceValue.textContent;
          let max = 0;
          [`₹${money.format(fromPaise / 100)}`, settleText].forEach((text) => {
            priceValue.textContent = text;
            max = Math.max(max, priceValue.getBoundingClientRect().width);
          });
          priceValue.textContent = original;
          return max;
        })();

        priceValue.style.display = 'inline-block';
        priceValue.style.minWidth = `${Math.ceil(widest)}px`;
        priceValue.classList.add('is-counting');

        const release = () => {
          priceValue.style.display = '';
          priceValue.style.minWidth = '';
          priceValue.classList.remove('is-counting');
        };

        const started = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - started) / DURATION);

          /* Smootherstep rather than an ease-out. An ease-out spends most of
           * its time almost arrived, which on a counter reads as a flick
           * followed by a wait. This leaves slowly, travels through the middle
           * where the numbers are worth watching, and settles without a stop. */
          const eased = t * t * t * (t * (t * 6 - 15) + 10);
          const at = fromPaise + (toPaise - fromPaise) * eased;

          if (t < 1) {
            priceValue.textContent = `₹${money.format(at / 100)}`;
            tween = requestAnimationFrame(step);
          } else {
            priceValue.textContent = settleText;
            release();
          }
        };
        tween = requestAnimationFrame(step);
      };

      const setPrice = (key, animate) => {
        const offered = key === 'offered';
        const target = offered ? priceBlock?.dataset.priceOffered : priceBlock?.dataset.pricePlain;

        if (priceValue && target) {
          const from = Number(offered ? priceBlock.dataset.pricePlainValue : priceBlock.dataset.priceOfferedValue);
          const to = Number(offered ? priceBlock.dataset.priceOfferedValue : priceBlock.dataset.pricePlainValue);
          if (animate) countTo(from, to, target);
          else priceValue.textContent = target;
        }

        if (priceOff && priceOff.dataset.offPlain) {
          priceOff.textContent = offered ? priceOff.dataset.offOffered : priceOff.dataset.offPlain;
        }
        priceBlock?.classList.toggle('is-offered', offered);
        if (priceNote) priceNote.hidden = !offered;
      };

      const reveal = (celebrate) => {
        result.hidden = false;
        card.classList.add('is-applied');
        button.disabled = true;
        button.textContent = button.dataset.labelApplied || button.textContent;
        setPrice('offered', celebrate);

        /* Thrown over the whole product column rather than the price itself:
         * the price is a 33px strip, and confetti inside it would be a line of
         * specks. Only on the tap — re-running it on every page load would
         * turn a reward into wallpaper, and choosing a size reloads this page. */
        if (celebrate) {
          /* The host is positioned only for the run. The canvas needs a
           * containing block, but leaving one on the column permanently would
           * silently re-home every absolutely placed element inside it. */
          const host = card.closest('.tittli-pdp__info') || priceBlock || card;
          host.classList.add('is-celebrating');
          window.tittliConfetti?.(host);
          setTimeout(() => host.classList.remove('is-celebrating'), 3200);
        }
      };

      const undo = () => {
        result.hidden = true;
        card.classList.remove('is-applied');
        button.disabled = false;
        button.textContent = button.dataset.labelApply || button.textContent;
        setPrice('plain', true);
        try {
          sessionStorage.setItem(COUPON_DISMISSED_KEY, code);
        } catch {
          /* no-op */
        }
      };

      remove?.addEventListener('click', undo);

      let dismissed = null;
      try {
        dismissed = sessionStorage.getItem(COUPON_DISMISSED_KEY);
      } catch {
        /* private window with site data blocked — it just will not persist */
      }

      /* Applied before the shopper does anything, unless they turned it off
       * in this session — and never on a product the offer does not reach,
       * where the card says how much more is needed and the headline price is
       * the one that will actually be charged.
       *
       * Without celebration: confetti belongs to a thing the shopper did, and
       * thrown on every page load it would be wallpaper. The price is simply
       * already the discounted one, which is what they would have got by
       * pressing the button anyway. */
      const qualifies = card.dataset.couponQualifies === 'true';
      if (qualifies && dismissed !== code) reveal(false);

      button.addEventListener('click', () => {
        reveal(true);
        try {
          sessionStorage.removeItem(COUPON_DISMISSED_KEY);
        } catch {
          /* no-op */
        }
      });
    });
  };

  const initProductChrome = () => {
    initStickyBar();
    centreColourway();
    initCoupons();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initProductChrome);
  else initProductChrome();
  document.addEventListener('shopify:section:load', initProductChrome);
}

/* The PDP rating links to Trustoo's review widget. The widget is injected by
 * Trustoo's script after load, and something on the page swallows the native
 * hash jump, so scroll to it explicitly — and if it has not arrived yet, wait
 * for it briefly rather than doing nothing. */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-scroll-to]');
  if (!link) return;
  event.preventDefault();

  const id = link.dataset.scrollTo;
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  /* An explicit offset rather than scrollIntoView: it clears the sticky
   * header, and it does not depend on the viewport reporting its height. */
  const go = (el) => {
    const header = document.querySelector('.section-header');
    const offset = (header ? header.getBoundingClientRect().height : 0) + 16;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - offset, behavior });
  };

  const target = document.getElementById(id);
  if (target) return go(target);

  const observer = new MutationObserver(() => {
    const el = document.getElementById(id);
    if (el) {
      observer.disconnect();
      go(el);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 8000);
});

/* ---------- fit switch ---------- */

/**
 * <tittli-fit-switch> — the regular/oversized segmented control. The options
 * are plain links and the navigation is the browser's, so this adds nothing
 * but the pressed state: the class that slides the thumb across while the
 * other product page loads.
 */
class TittliFitSwitch extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || !this.contains(link)) return;
      /* Modified clicks open a new tab and leave this page where it is, so
       * the switch must not look as though it moved. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      this.classList.add('is-switching');
    });

    /* Coming back to a bfcached page restores the DOM as it was left —
     * mid-slide, showing the fit the shopper navigated away to. */
    this.resetOnRestore = (event) => {
      if (event.persisted) this.classList.remove('is-switching');
    };
    window.addEventListener('pageshow', this.resetOnRestore);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.resetOnRestore);
  }
}

if (!customElements.get('tittli-fit-switch')) {
  customElements.define('tittli-fit-switch', TittliFitSwitch);
}
